import { useMsal } from "@azure/msal-react";
import { Anchor, Flex, Text } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useRouter } from "@tanstack/react-router";
import {
    MantineReactTable,
    type MRT_ColumnFiltersState,
    type MRT_RowVirtualizer,
    type MRT_SortingState,
    useMantineReactTable,
} from "mantine-react-table";
import { type UIEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CTS_USER_CHAT } from "src/db";
import { useSearchAfterPagination } from "src/hooks/useSearchAfterPagination";
import { useUser } from "src/hooks/useUser";
import { postAPI } from "src/utils/fetch";
import { useAdmin } from "src/utils/useAdmin";

import FeedbackModal from "./FeedbackModal";
import { flattenMetadataHits } from "./MetadataDocuments/utils";
import { metadataFeedbackColumns } from "./MetadataFeedback/columns";
import type { FeedbackSchema } from "./MetadataFeedback/types";
import { PdfFocusProvider } from "./PDFViewer/context";
import TopToolbarCustomActions from "./TopToolbarCustomActions";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const OPENSEARCH_DASHBOARD_URL = import.meta.env.VITE_OPENSEARCH_DASHBOARD_URL;
const MetadataFeedback = () => {
    const columns = metadataFeedbackColumns;

    const rowVirtualizerInstanceRef =
        useRef<MRT_RowVirtualizer<HTMLDivElement, HTMLTableRowElement>>(null);

    const [sorting, setSorting] = useState<MRT_SortingState>([]);
    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);

    const { isLoading: isUserLoading } = useUser();
    const isAdmin = useAdmin();
    if (!isUserLoading && !isAdmin) {
        throw Error("Unauthorized");
    }

    const { instance } = useMsal();

    // Get current URL parameters
    const location = useLocation();
    const router = useRouter();
    const selectedId = location.search?.id;

    const buildPayload = useCallback(() => {
        const normalizeMemoryValue = (input: unknown) => {
            if (typeof input !== "string") {
                return input;
            }

            const trimmed = input.trim().toLowerCase();
            if (trimmed === "yes") return "true";
            if (trimmed === "no") return "false";
            return input;
        };

        return (
            columnFilters
                ?.filter((val) =>
                    Array.isArray(val.value)
                        ? val.value.filter(Boolean).length > 0
                        : Boolean(val.value),
                )
                .map((filter) => {
                    const key = filter.id.split("__")[1] ?? filter.id;

                    let value = filter.value;
                    if (key === "timestamp" && Array.isArray(value)) {
                        value = value.map((item) => (item == null ? "" : item));
                    }

                    if (key === "request_options.memory_enabled") {
                        if (Array.isArray(value)) {
                            value = value.map((item) => normalizeMemoryValue(item));
                        } else {
                            value = normalizeMemoryValue(value);
                        }
                    }

                    return {
                        key,
                        value,
                    };
                }) ?? []
        );
    }, [columnFilters]);

    const { data, fetchNextPage, isFetching, isLoading, isError } = useSearchAfterPagination({
        queryKey: ["metadata-feedback", CTS_USER_CHAT, sorting, columnFilters],
        index: CTS_USER_CHAT,
        sorting,
        columnFilters,
        buildPayload,
    });

    const flatData = useMemo(() => flattenMetadataHits(data, isError), [data, isError]);

    // Simple query to fetch specific feedback item by ID when not in loaded data
    const { data: specificFeedbackData } = useQuery({
        queryKey: ["specific-feedback", selectedId],
        queryFn: async () => {
            const PATH = `v1/search/${CTS_USER_CHAT}/metadata/docs?max_result=1`;
            const url = new URL(PATH, BACKEND_URL);
            const response = await postAPI({
                url: url.toString(),
                instance,
                // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
                payload: [{ key: "_id", value: selectedId }] as any,
            });
            const data = await response.json();
            return data?.hits?.hits?.[0] || null;
        },
        enabled: Boolean(selectedId && !flatData.find((doc) => doc._id === selectedId)),
        refetchOnWindowFocus: false,
    });

    // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
    const totalDBRowCount = (data as any)?.pages?.[0]?.hits?.total?.value ?? 0;

    // Use count directly from main query response
    const displayCount = totalDBRowCount;

    const fetchMoreOnBottomReached = useCallback(
        (containerRefElement?: HTMLDivElement | null) => {
            if (containerRefElement) {
                const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
                if (
                    scrollHeight - scrollTop - clientHeight < 400 &&
                    !isFetching &&
                    flatData.length < totalDBRowCount
                ) {
                    fetchNextPage();
                }
            }
        },
        [fetchNextPage, isFetching, flatData.length, totalDBRowCount],
    );
    // biome-ignore lint/correctness/useExhaustiveDependencies: sorting is required for correct behavior
    useEffect(() => {
        if (rowVirtualizerInstanceRef.current) {
            try {
                rowVirtualizerInstanceRef.current.scrollToIndex(0);
            } catch (e) {
                console.error(e);
            }
        }
    }, [sorting]);

    useEffect(() => {
        fetchMoreOnBottomReached(tableContainerRef.current);
    }, [fetchMoreOnBottomReached]);

    const [rowVirtualizerRef, setRowVirtualizerRef] = useState<MRT_RowVirtualizer<
        HTMLDivElement,
        HTMLTableRowElement
    > | null>(null);
    const tableContainerRef = useRef<HTMLDivElement>(null); //we can get access to the underlying TableContainer element and react to its scroll events

    // Find selected row based on URL parameter
    const selectedRow = useMemo(() => {
        if (!selectedId) return null;

        // First check if the item is already in loaded data
        const foundInLoadedData = flatData.find((doc) => doc._id === selectedId);
        if (foundInLoadedData) return foundInLoadedData;

        // If not found in loaded data, use specific feedback data
        if (specificFeedbackData) {
            return {
                ...specificFeedbackData,
                fields: specificFeedbackData._source?.metadata,
            };
        }

        return null;
    }, [selectedId, flatData, specificFeedbackData]);

    // Handle row click - update URL with ID
    const handleRowClick = useCallback(
        (row: FeedbackSchema) => {
            router.navigate({
                to: "/feedback",
                // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
                search: { id: row._id } as any,
            });
        },
        [router],
    );

    // Handle modal close - remove ID from URL
    const handleModalClose = useCallback(() => {
        router.navigate({
            to: "/feedback",
            // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
            search: { id: undefined } as any,
        });
    }, [router]);

    const table = useMantineReactTable<FeedbackSchema>({
        layoutMode: "grid",
        columns,
        data: flatData ?? [],
        enableColumnOrdering: true,
        enablePinning: true,
        enableBottomToolbar: false,
        renderToolbarAlertBannerContent: () => <></>,
        enableColumnResizing: true,
        enableColumnFilterModes: false,
        // TODO This used to specify a filter function, but this property doesn't actually exist.
        // It should be converted into using `filterFns`.
        // filterFn: "includesString",
        enablePagination: false,
        enableRowNumbers: true,
        enableClickToCopy: false,
        getRowId: (row) => {
            return `${row._id}`;
        },
        renderTopToolbarCustomActions: () => (
            <>
                <TopToolbarCustomActions
                    // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
                    table={table as any}
                    index={CTS_USER_CHAT}
                    // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
                    data={data as any}
                    columnFilters={columnFilters}
                    // TODO This is a required prop but was not set previously, so it is currently set to false,
                    // but needs to be revisited.
                    isLoading={false}
                />
                <Flex align="center" gap="md">
                    <Anchor href={OPENSEARCH_DASHBOARD_URL} target="_blank">
                        Opensearch Dashboard
                    </Anchor>
                    <Text size="sm" c="dimmed">
                        {`Total: ${displayCount}, (Showing ${flatData.length})`}
                    </Text>
                </Flex>
            </>
        ),
        mantineToolbarAlertBannerProps: {
            style: {
                display: "none",
                background: "#f0f0f0",
                fontWeight: "bold",
            },
        },
        onColumnFiltersChange: setColumnFilters,
        enableGlobalFilter: false,
        enableFullScreenToggle: false,
        enableSelectAll: false,
        selectAllMode: "page",
        enableGrouping: true,
        enableRowVirtualization: true,
        enableDensityToggle: false,
        mantineTableBodyRowProps: ({ row }) => ({
            onClick: () => handleRowClick(row.original as FeedbackSchema),
            sx: {
                cursor: "pointer",
            },
        }),
        mantineTableContainerProps: {
            ref: tableContainerRef, //get access to the table container element
            style: { padding: 0, borderRadius: 0, maxHeight: "80vh" },
            onScroll: (
                event: UIEvent<HTMLDivElement>, //add an event listener to the table container element
            ) => fetchMoreOnBottomReached(event.target as HTMLDivElement),
        },
        mantinePaperProps: { style: { borderRadius: 0, zIndex: 102 } },
        onSortingChange: setSorting,
        state: { isLoading, sorting },
        initialState: {
            showGlobalFilter: true,
            density: "xs",
            showColumnFilters: true,
        },
        rowVirtualizerInstanceRef: ((instance) => {
            setRowVirtualizerRef(instance);
            // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
        }) as any,
        rowVirtualizerOptions: {
            overscan: 10,
            onScroll: (event) => {
                const { scrollOffset, scrollHeight, clientHeight } = event;
                if (scrollOffset + clientHeight >= scrollHeight - 200) {
                    // Load more data when user scrolls to bottom
                    // Implement your data fetching logic here
                }
            },
            // biome-ignore lint/suspicious/noExplicitAny: TODO: This is a temporary fix to enforce TS typing.
        } as any,
    });
    // biome-ignore lint/correctness/useExhaustiveDependencies: sorting and rowVirtualizerRef are required
    useEffect(() => {
        if (rowVirtualizerRef) {
            rowVirtualizerRef.scrollToIndex(0);
        }
    }, [sorting, rowVirtualizerRef]);

    return (
        <PdfFocusProvider>
            <MantineReactTable table={table} />
            <FeedbackModal rowData={selectedRow} onClose={handleModalClose} />
        </PdfFocusProvider>
    );
};

export default MetadataFeedback;
